// CARATJEW Verified Buyer application page + reusable Verified badge.
// Matches the existing luxe black-on-white B2B aesthetic — no new UI kit.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck, ArrowLeft, ArrowUpRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/api";
import { useAuth } from "@/auth";

const BUYER_TYPES = [
  "Jewellery Retailer",
  "Jewellery Chain",
  "Wholesaler",
  "Importer",
  "Distributor",
  "Jewellery Brand",
  "Buying House",
  "Sourcing / Purchasing Professional",
  "Other",
];

const PRODUCT_CATEGORIES = [
  "Natural Diamonds",
  "Lab-Grown Diamonds",
  "Diamond Jewellery",
  "Gold Jewellery",
  "Gemstones",
  "Fine Jewellery",
  "Fashion / Imitation Jewellery",
  "Other",
];

const STATUS_META = {
  not_applied: { label: "Verification Not Started", tone: "muted" },
  pending: { label: "Verification Pending", tone: "warn" },
  more_info: { label: "More Information Required", tone: "info" },
  verified: { label: "CARATJEW Verified Buyer", tone: "ok" },
  rejected: { label: "Application Not Approved", tone: "danger" },
};

// ---------- Reusable badge ----------
export function VerifiedBadge({ size = 12, label = true, testId }) {
  return (
    <span className="verified-badge" data-testid={testId || "verified-badge"} title="CARATJEW Verified Buyer">
      <BadgeCheck size={size}/>
      {label && <span>Verified</span>}
    </span>
  );
}

export function VerificationPill({ status, testId }) {
  const meta = STATUS_META[status] || STATUS_META.not_applied;
  return (
    <span className={`verify-pill tone-${meta.tone}`} data-testid={testId}>
      {status === "verified" && <BadgeCheck size={12}/>}
      {meta.label}
    </span>
  );
}

// ---------- Verify page ----------

const EMPTY_FORM = {
  full_name: "",
  job_title: "",
  company_name: "",
  country: "",
  city: "",
  company_website: "",
  business_email: "",
  business_phone: "",
  buyer_type: "",
  product_categories: [],
  sourcing_markets: "",
  company_description: "",
  linkedin: "",
};

export default function Verify() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "not_applied", application: null, loading: true });
  const [form, setForm] = useState(EMPTY_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [okBanner, setOkBanner] = useState("");

  useEffect(() => {
    let alive = true;
    api.myVerification()
      .then((r) => {
        if (!alive) return;
        setState({ ...r, loading: false });
        if (r.application) {
          setForm({
            full_name: r.application.full_name || "",
            job_title: r.application.job_title || "",
            company_name: r.application.company_name || "",
            country: r.application.country || "",
            city: r.application.city || "",
            company_website: r.application.company_website || "",
            business_email: r.application.business_email || "",
            business_phone: r.application.business_phone || "",
            buyer_type: r.application.buyer_type || "",
            product_categories: r.application.product_categories || [],
            sourcing_markets: r.application.sourcing_markets || "",
            company_description: r.application.company_description || "",
            linkedin: r.application.linkedin || "",
          });
        } else if (user) {
          setForm((f) => ({
            ...f,
            full_name: user.name || "",
            business_email: user.email || "",
          }));
        }
        setHydrated(true);
      })
      .catch(() => { setState({ status: "not_applied", application: null, loading: false }); setHydrated(true); });
    return () => { alive = false; };
    // Only hydrate ONCE — do not re-run on user identity changes to avoid
    // clobbering in-progress edits (see iteration_20 rca).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readOnly = state.status === "pending" || state.status === "verified";
  const canEdit = !readOnly;
  const isResubmit = state.status === "more_info" || state.status === "rejected";

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleCategory = (c) =>
    setForm((f) => ({
      ...f,
      product_categories: f.product_categories.includes(c)
        ? f.product_categories.filter((x) => x !== c)
        : [...f.product_categories, c],
    }));

  const requiredFilled = useMemo(() => (
    form.full_name.trim() && form.job_title.trim() && form.company_name.trim() &&
    form.country.trim() && form.city.trim() && form.business_email.trim() &&
    form.business_phone.trim() && form.buyer_type &&
    form.product_categories.length > 0
  ), [form]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setOkBanner("");
    if (!requiredFilled) {
      setError("Please fill in every required field before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        company_website: form.company_website.trim() || null,
        sourcing_markets: form.sourcing_markets.trim() || null,
        company_description: form.company_description.trim() || null,
        linkedin: form.linkedin.trim() || null,
      };
      const r = await api.submitVerification(payload);
      setState({ ...r, loading: false });
      setOkBanner("Application submitted. Our team will review it shortly.");
      try { await refresh(); } catch {}
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="verify-wrap" data-testid="verify-not-logged-in">
        <p>Please sign in first to apply for Verified Buyer status.</p>
        <Link className="button button-dark" to="/login">Sign in <ArrowUpRight size={14}/></Link>
      </div>
    );
  }

  if (user.role !== "buyer") {
    return (
      <div className="verify-wrap" data-testid="verify-wrong-role">
        <h1>Verified Buyer is for buyer accounts</h1>
        <p>Your account is currently a <b>{user.role}</b>. Only buyer accounts can apply for CARATJEW Verified Buyer status.</p>
        <Link className="button button-outline" to="/">Back to home <ArrowLeft size={14}/></Link>
      </div>
    );
  }

  return (
    <div className="verify-wrap" data-testid="verify-page">
      <style>{STYLES}</style>
      <header className="verify-head">
        <div className="eyebrow">CARATJEW VERIFIED BUYER</div>
        <h1>Apply as a Verified Buyer</h1>
        <p className="verify-intro">
          CARATJEW Verified Buyer status is reserved for professional jewellery trade
          buyers — retailers, wholesalers, importers, distributors, jewellery brands,
          buying houses and sourcing / purchasing professionals. Verified accounts get
          a premium badge across the platform and priority access to exhibitor
          meetings and live booths.
        </p>
        <VerificationPill status={state.status} testId="verify-current-status"/>
      </header>

      {state.status === "verified" && (
        <div className="verify-banner ok" data-testid="verify-banner-verified">
          <CheckCircle2 size={16}/>&nbsp;You are a <b>CARATJEW Verified Buyer</b>. Your badge is shown across your dashboard, inquiries and meetings.
        </div>
      )}
      {state.status === "pending" && (
        <div className="verify-banner info" data-testid="verify-banner-pending">
          Your application is being reviewed. We'll email you as soon as an admin makes a decision. No further action is needed for now.
        </div>
      )}
      {state.status === "more_info" && (
        <div className="verify-banner warn" data-testid="verify-banner-more-info">
          <AlertTriangle size={16}/>&nbsp;<b>More information required.</b> Please update the fields below and resubmit.
        </div>
      )}
      {state.status === "rejected" && (
        <div className="verify-banner danger" data-testid="verify-banner-rejected">
          Your previous application was not approved. You may update and resubmit at any time.
        </div>
      )}
      {okBanner && <div className="verify-banner ok" data-testid="verify-banner-ok">{okBanner}</div>}
      {error && <div className="verify-banner danger" data-testid="verify-banner-error">{error}</div>}

      {!hydrated ? (
        <div className="verify-banner info" data-testid="verify-loading">Loading your application…</div>
      ) : (
      <form className="verify-form" onSubmit={submit} data-testid="verify-form">
        <fieldset disabled={readOnly || submitting}>
          <div className="verify-grid">
            <Field label="Full Name" required testId="v-full-name">
              <input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} maxLength={120} required data-testid="v-input-full-name"/>
            </Field>
            <Field label="Job Title / Designation" required testId="v-job-title">
              <input value={form.job_title} onChange={(e) => update("job_title", e.target.value)} maxLength={120} required data-testid="v-input-job-title"/>
            </Field>
            <Field label="Company Name" required testId="v-company-name">
              <input value={form.company_name} onChange={(e) => update("company_name", e.target.value)} maxLength={160} required data-testid="v-input-company-name"/>
            </Field>
            <Field label="Buyer Type" required testId="v-buyer-type">
              <select value={form.buyer_type} onChange={(e) => update("buyer_type", e.target.value)} required data-testid="v-input-buyer-type">
                <option value="">— select —</option>
                {BUYER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Country" required testId="v-country">
              <input value={form.country} onChange={(e) => update("country", e.target.value)} maxLength={80} required data-testid="v-input-country"/>
            </Field>
            <Field label="City" required testId="v-city">
              <input value={form.city} onChange={(e) => update("city", e.target.value)} maxLength={80} required data-testid="v-input-city"/>
            </Field>
            <Field label="Business Email" required testId="v-business-email">
              <input type="email" value={form.business_email} onChange={(e) => update("business_email", e.target.value)} required data-testid="v-input-business-email"/>
            </Field>
            <Field label="Business Phone" required testId="v-business-phone">
              <input type="tel" value={form.business_phone} onChange={(e) => update("business_phone", e.target.value)} maxLength={40} required data-testid="v-input-business-phone"/>
            </Field>
            <Field label="Company Website" testId="v-website" full>
              <input type="url" placeholder="https://" value={form.company_website} onChange={(e) => update("company_website", e.target.value)} maxLength={200} data-testid="v-input-website"/>
            </Field>
          </div>

          <div className="verify-block">
            <div className="verify-block-title">Product categories you source *</div>
            <div className="verify-chips" data-testid="v-categories">
              {PRODUCT_CATEGORIES.map((c) => {
                const on = form.product_categories.includes(c);
                return (
                  <button type="button" key={c} className={on ? "chip on" : "chip"} onClick={() => toggleCategory(c)} data-testid={`v-category-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="verify-grid">
            <Field label="Main sourcing markets (optional)" testId="v-sourcing" full>
              <input placeholder="e.g. India, UAE, Belgium" value={form.sourcing_markets} onChange={(e) => update("sourcing_markets", e.target.value)} maxLength={400} data-testid="v-input-sourcing"/>
            </Field>
            <Field label="LinkedIn profile or company page (optional)" testId="v-linkedin" full>
              <input type="url" placeholder="https://linkedin.com/..." value={form.linkedin} onChange={(e) => update("linkedin", e.target.value)} maxLength={200} data-testid="v-input-linkedin"/>
            </Field>
            <Field label="Short company / business description" testId="v-description" full>
              <textarea rows={4} value={form.company_description} onChange={(e) => update("company_description", e.target.value)} maxLength={1200} data-testid="v-input-description"/>
            </Field>
          </div>

          <p className="verify-fineprint">
            We ask only for professional information. We will never request scanned IDs,
            passports or other unnecessary personal documents. The final verification
            decision always belongs to a CARATJEW admin — submitting this form does
            not automatically grant Verified status.
          </p>
        </fieldset>

        <div className="verify-actions">
          <button type="button" className="button button-outline" onClick={() => navigate("/dashboard/buyer")} data-testid="v-cancel">
            <ArrowLeft size={14}/>&nbsp;Back to dashboard
          </button>
          {canEdit && (
            <button type="submit" className="button button-dark" disabled={submitting || !requiredFilled} data-testid="v-submit">
              {submitting ? "Submitting…" : isResubmit ? "Resubmit application" : "Submit application"}
            </button>
          )}
        </div>
      </form>
      )}
    </div>
  );
}

function Field({ label, required, children, testId, full }) {
  return (
    <label className={`verify-field${full ? " full" : ""}`} data-testid={testId}>
      <span>{label}{required && <em>*</em>}</span>
      {children}
    </label>
  );
}

const STYLES = `
.verify-wrap{max-width:900px;margin:0 auto;padding:60px 6vw 120px}
.verify-head .eyebrow{color:#638d85}
.verify-head h1{font:500 44px/1.05 'Playfair Display',serif;letter-spacing:-.02em;margin:14px 0 18px}
.verify-intro{font-size:14px;line-height:1.75;color:#555;max-width:640px;margin:0 0 20px}
.verify-pill{display:inline-flex;align-items:center;gap:6px;font:10px 'DM Mono',monospace;letter-spacing:.06em;padding:6px 10px;border:1px solid var(--line);text-transform:uppercase}
.verify-pill.tone-ok{border-color:#8ba695;background:#eef4ef;color:#3f5b49}
.verify-pill.tone-warn{border-color:#c9a06f;background:#f7efe0;color:#7c5729}
.verify-pill.tone-info{border-color:#8ea4b3;background:#e9eef4;color:#3e5a70}
.verify-pill.tone-danger{border-color:#c98d84;background:#f6e4e0;color:#7a382e}
.verify-pill.tone-muted{border-color:var(--line);background:#f4f4f0;color:#666}
.verify-banner{margin:22px 0;padding:14px 18px;border:1px solid var(--line);background:#fbfbfa;font-size:13px;display:flex;align-items:center;gap:4px}
.verify-banner.ok{border-color:#8ba695;background:#eef4ef;color:#3f5b49}
.verify-banner.warn{border-color:#c9a06f;background:#f7efe0;color:#7c5729}
.verify-banner.info{border-color:#8ea4b3;background:#e9eef4;color:#3e5a70}
.verify-banner.danger{border-color:#c98d84;background:#f6e4e0;color:#7a382e}
.verify-form fieldset{border:0;padding:0;margin:32px 0 0}
.verify-form fieldset:disabled{opacity:.6}
.verify-grid{display:grid;grid-template-columns:1fr 1fr;gap:18px 22px}
.verify-field{display:flex;flex-direction:column;gap:8px}
.verify-field.full{grid-column:1/-1}
.verify-field>span{font:10px 'DM Mono',monospace;letter-spacing:.08em;color:#555}
.verify-field em{color:#c4664f;font-style:normal;margin-left:3px}
.verify-field input,.verify-field select,.verify-field textarea{border:0;border-bottom:1px solid var(--line);background:transparent;padding:10px 0;font-size:14px;font-family:inherit;outline:none;color:var(--ink);border-radius:0}
.verify-field input:focus,.verify-field select:focus,.verify-field textarea:focus{border-bottom-color:var(--ink)}
.verify-field textarea{border:1px solid var(--line);padding:10px 12px;resize:vertical;min-height:80px}
.verify-block{margin:28px 0}
.verify-block-title{font:10px 'DM Mono',monospace;letter-spacing:.08em;color:#555;margin-bottom:12px}
.verify-chips{display:flex;flex-wrap:wrap;gap:8px}
.chip{font-family:inherit;background:transparent;border:1px solid var(--line);padding:8px 14px;font-size:12px;cursor:pointer;color:var(--ink)}
.chip.on{background:var(--ink);color:#fff;border-color:var(--ink)}
.chip:hover:not(.on){border-color:var(--ink)}
.verify-fineprint{margin-top:26px;font-size:11px;line-height:1.7;color:#888}
.verify-actions{display:flex;justify-content:space-between;align-items:center;gap:14px;margin-top:26px;flex-wrap:wrap}
.verified-badge{display:inline-flex;align-items:center;gap:4px;background:linear-gradient(135deg,#f0f5ee 0%,#e8efe6 100%);border:1px solid #8ba695;color:#3f5b49;font:9px 'DM Mono',monospace;letter-spacing:.06em;padding:2px 7px 2px 5px;text-transform:uppercase;white-space:nowrap;vertical-align:middle}
.verified-badge svg{color:#638d85}
@media(max-width:700px){
  .verify-wrap{padding:40px 6vw 90px}
  .verify-head h1{font-size:34px}
  .verify-grid{grid-template-columns:1fr;gap:16px}
  .verify-actions{flex-direction:column-reverse;align-items:stretch}
  .verify-actions .button{width:100%;justify-content:center}
}
`;
